import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAgencyPublicData } from '../services/agencyPublicService';
import type { AgencyPublicData } from '../services/agencyPublicService';
import { agencyLogin as agencyLoginService, getProfile as getProfileService } from '../services/authService';
import { isTwoFactorChallenge } from '../services/twoFactorService';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/slices/authSlice';
import { StorageManager } from '../utils/storageManager';
import SeoLandingPageRoute from './SeoLandingPageRoute';
import { SEO_LANDING_SLUGS } from '../constants/seoRoutes';

const AgencyLogin = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const location = useLocation();
  const resolvedSlug = useMemo(() => {
    const search = new URLSearchParams(location.search);
    const qsSlug = search.get('agency') || search.get('agencySlug');
    if (agencySlug && agencySlug !== 'checkin') return agencySlug;
    if (qsSlug) return qsSlug;
    return '';
  }, [agencySlug, location.search]);
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyPrompt, setVerifyPrompt] = useState(false);
  const [agencyData, setAgencyData] = useState<AgencyPublicData | null>(null);
  const [fetchingAgency, setFetchingAgency] = useState(true);
  const [agencyError, setAgencyError] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Safety net: if a marketing SEO slug is routed here, render SEO page instead.
  if (agencySlug && SEO_LANDING_SLUGS.has(agencySlug)) {
    return <SeoLandingPageRoute />;
  }

  useEffect(() => {
    const checkExistingAuth = async () => {
      const storedToken = StorageManager.getToken();
      if (storedToken) {
        try {
          // Check if user is already authenticated
          const response = await getProfileService(false);
          if (response.user && response.agency?.slug === resolvedSlug) {
            // User is authenticated and belongs to this agency, redirect to dashboard
            navigate(`/agencies/${resolvedSlug}/dashboard`);
            return;
          }
        } catch (error) {
          // Token is invalid, clear it and continue with login form
          StorageManager.removeToken();
        }
      }
    };

    const fetchAgencyData = async () => {
      if (!resolvedSlug) {
        setAgencyError('Agency not specified');
        setFetchingAgency(false);
        return;
      }

      try {
        const data = await getAgencyPublicData(resolvedSlug);
        setAgencyData(data);
      } catch (err: any) {
        console.error('Failed to fetch agency data:', err);
        setAgencyError('Agency not found');
      } finally {
        setFetchingAgency(false);
      }
    };

    // Check authentication first, then fetch agency data
    checkExistingAuth().then(() => {
      fetchAgencyData();
    });
  }, [resolvedSlug, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedSlug) {
      setError('Agency not specified');
      return;
    }
    
    setLoading(true);
    setError('');
    setVerifyPrompt(false);
    try {
      const response = await agencyLoginService(resolvedSlug, {
        email: form.email,
        password: form.password
      });

      if (isTwoFactorChallenge(response)) {
        const mode = response.requiresTwoFactorSetup ? 'setup' : 'verify';
        navigate(`/two-factor?mode=${mode}`);
        return;
      }

      dispatch(loginSuccess({
        user: response.user!,
        token: response.token!,
        agency: response.agency
      }));
      
      // Navigate to agency dashboard
      if (response.agency?.slug) {
        navigate(`/agencies/${response.agency.slug}/dashboard`);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg);
      if (msg.toLowerCase().includes('verify your email')) setVerifyPrompt(true);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingAgency) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (agencyError || !agencyData) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Alert severity="error" sx={{ width: '100%' }}>
              {agencyError || 'Agency not found'}
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              sx={{ mt: 2 }}
            >
              Go to Regular Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Agency Banner */}
          {agencyData.bannerImageData && agencyData.bannerImageType && (
            <Box
              sx={{
                width: '100%',
                mb: 3,
                borderRadius: 1,
                overflow: 'hidden',
                maxHeight: '200px',
              }}
            >
              <img
                src={`data:${agencyData.bannerImageType};base64,${agencyData.bannerImageData}`}
                alt={`${agencyData.name} banner`}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}

          <Typography component="h1" variant="h5">
            Sign in to {agencyData.name}
          </Typography>

          {error && (
            <Alert severity={verifyPrompt ? 'warning' : 'error'} sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Button
                href={`/${agencySlug}/forgot-password`}
                variant="text"
                size="small"
              >
                Forgot Password?
              </Button>
            </Box>
            
            {verifyPrompt && (
              <Box sx={{ mt: 2 }}>
                <Button href="/verify-email" variant="outlined" fullWidth>
                  Resend Verification Email
                </Button>
              </Box>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Need access? Contact your agency administrator.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AgencyLogin;

